import { Box, Grid } from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@mui/material';
import TextField from '@mui/material/TextField';
import DropdownFilter from 'components/DropdownFilter';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import WoRoSearchBar from 'components/WoRoSearchBar/WoRoSearchBar';
import { useGetVehiclesQuery } from "features/vehicles/vehicleSlice";
import { IconCar } from 'icons';
import { useMemo, useRef, useState } from 'react';
import { arrayToObject } from 'utils/object-util';
import { AssetSearchBarStyles } from './AssetSearchBarStyles';
import DataSearchBar from 'components/DataSearchBar';

const searchTypesObj = {
    ALL: 2,
    IDENTIFIER: 1,
    WO_RO: 3,
    WR: 4
}

const AssetSearchBar = ({ assets, setAssets, assetsList = [], searchVal, onSearch, woRoTerms = [], onWoRoTermsChange, wrTerms = [], onWrTermsChange }) => {
    const classes = AssetSearchBarStyles();

    let { data: vehicles, error: vehicleError, isFetching: isFetchingVehicles, refetch: refetchVehicles } = useGetVehiclesQuery({ includeHistory: true });

    vehicles = vehicles || [];
    const inputRef = useRef();

    const [searchType, setSearchType] = useState(searchTypesObj.ALL);

    const searchTypes = [
        { id: searchTypesObj.ALL, name: 'All' },
        { id: searchTypesObj.IDENTIFIER, name: 'Identifier' },
        ...(onWoRoTermsChange ? [{ id: searchTypesObj.WO_RO, name: 'WO/RO' }] : []),
        ...(onWrTermsChange ? [{ id: searchTypesObj.WR, name: 'WR' }] : []),
    ]

    const vehiclesByVins = useMemo(() => arrayToObject(vehicles, 'descriptor'), [vehicles]);
    const vehiclesByAssetId = useMemo(() => arrayToObject(vehicles, 'assetId'), [vehicles]);

    const getOptionLabel = (option) => {
        return `${option.descriptor || ''} ${option.assetId || ''} ${option.licensePlate || ''}`;
    }

    const filterOptions = createFilterOptions({
        ignoreCase: true,
        limit: 100,
    });

    const handleChange = (newValue) => {
        setAssets(newValue);
    }

    const onChangeSearchType = (val) => {
        setSearchType(val);
        if (val != searchTypesObj.IDENTIFIER) setAssets([]);
        if (val != searchTypesObj.ALL) onSearch('');
        if (val != searchTypesObj.WO_RO) onWoRoTermsChange?.([]);
        if (val != searchTypesObj.WR) onWrTermsChange?.([]);
    }

    const renderOption = (props, option) => {
        return (
            <Box  {...props}>
                <Grid container xs={12} alignItems='center' justifyContent='space-between'>
                    <Grid item  >
                        <Grid container alignItems='center'>
                            <Grid item className={classes.itemIcon}>
                                <IconCar />
                            </Grid>
                            <Grid item>
                                <Grid container direction='column'>
                                    {option?.descriptor ? <Grid item >
                                        <label className={classes.itemLabel}> VIN: </label>
                                        {option?.descriptor}
                                    </Grid> : <></>}
                                    {option?.assetId ? <Grid item >
                                        <label className={classes.itemLabel}> Asset ID: </label>
                                        {option?.assetId}
                                    </Grid> : <></>}
                                    {option?.licensePlate ? <Grid item >
                                        <label className={classes.itemLabel}> License Plate: </label>
                                        {option?.licensePlate}
                                    </Grid> : <></>}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        )
    }

    return (
        <Grid container justifyContent='space-between' alignItems='center' spacing={1} >

            <Grid item xs={2} >
                <DropdownFilter
                    values={searchTypes}
                    onSelect={(val) => onChangeSearchType(val)}
                    value={searchType}
                    title='Search Type'
                    variant='outlined'
                    sx={{ height: '100%' }}
                />
            </Grid>

            <Grid item xs={10} >
                {searchType == searchTypesObj.IDENTIFIER ? <Autocomplete
                    id="asset-selector"
                    multiple
                    filterSelectedOptions
                    disableCloseOnSelect
                    value={assets}
                    options={assetsList}
                    getOptionLabel={(option) => getOptionLabel(option)}
                    getOptionKey={(option) => option?.id}
                    filterOptions={filterOptions}
                    isOptionEqualToValue={(option, value) => {
                        return option?.id == value?.id
                    }}
                    onChange={(event, newInputValue) => {
                        handleChange(newInputValue);
                    }}
                    renderOption={(props, option) => renderOption(props, option)}
                    sx={{ padding: 0 }}
                    renderInput={(params) => (
                        <div>
                            <TextField
                                {...params}
                                fullWidth
                                placeholder={assets?.length ? null : 'Select or Paste a List of Assets'}
                                inputRef={inputRef}
                                onPaste={(e) => {
                                    const vins = e.clipboardData.getData('Text').split("\n");
                                    let vinsToAdd = [];
                                    vins?.map(vin => {
                                        if (vin && vin != '') {
                                            const cleanVin = vin.split('\r').join("").trim();
                                            let vehicle = vehiclesByVins?.[cleanVin] ?? vehiclesByAssetId?.[cleanVin];
                                            if (vehicle && !assets?.find(a => a?.id == vehicle?.id)) {
                                                vinsToAdd.push(vehicle);
                                            }
                                        }
                                    });
                                    if (vinsToAdd.length > 0) {
                                        setAssets([...assets, ...vinsToAdd]);
                                        inputRef.current.blur();
                                    }
                                }} />
                            < LoadingSpinner loading={isFetchingVehicles} />
                        </div>
                    )}
                    className={classes.autoComplete}
                /> : searchType == searchTypesObj.WO_RO ?
                    <WoRoSearchBar
                        woRoTerms={woRoTerms}
                        onWoRoTermsChange={onWoRoTermsChange}
                    /> : searchType == searchTypesObj.WR ?
                    <WoRoSearchBar
                        woRoTerms={wrTerms}
                        onWoRoTermsChange={onWrTermsChange}
                        placeholder='Search or Paste a List of WR Numbers'
                    /> :
                    <DataSearchBar
                        onSearch={onSearch}
                        searchVal={searchVal}
                    />
                }
            </Grid>
        </Grid >
    );
}
export default AssetSearchBar;